import { Crown } from 'lucide-react'
import { teamDisplayName, teamPickerName, type TeamId } from '../teams'
import ScorePicker from './ScorePicker'
import GoldenScoreRow from './gameEdit/GoldenScoreRow'
import WinProbabilityBar from './WinProbabilityBar'
import type { FixtureStatus, MatchForecast } from '../api/football'
import type { Game, TeamFormResult } from '../types'
import { useBankerPickedLanding } from '../useLeaderLanding'
import './GameEditScreen.css'

interface GameEditScreenProps {
  game: Game
  homeScore: number
  awayScore: number
  homeTeamForm: TeamFormResult[]
  awayTeamForm: TeamFormResult[]
  onScoreChange: (homeScore: number, awayScore: number) => void
  isBanker: boolean
  onToggleBanker: () => void
  // The fixture currently set as Banker, only set while a switch away from it
  // to `game` is pending confirmation. Null means no confirmation is showing.
  pendingBankerSwitchFrom: { homeTeam: TeamId; awayTeam: TeamId } | null
  onConfirmBankerSwitch: () => void
  onCancelBankerSwitch: () => void
  /**
   * The gameweek's stored forecast for this fixture, or null/absent when there is none.
   *
   * A game with no forecast renders **nothing** — no header, no bar, no skeleton, no "not
   * available" copy. The modal is complete without it, and its layout must not shift because a
   * neighbouring fixture in the cycle has one and this one doesn't.
   */
  forecast?: MatchForecast | null
  /**
   * The fixture's current status. The bar is pre-match only: it disappears the instant a fixture
   * goes live, because a probability that stops updating while a match is being played reads as
   * live when it is not — it will sit at "58% home" while the away side is 2-0 up.
   *
   * Absent means `scheduled`. The picker is only reachable before the deadline, so that is the
   * only state most callers ever pass.
   */
  fixtureStatus?: FixtureStatus
  /**
   * The score this fixture is already going to end on, in the modes where that is knowable.
   *
   * Null or absent in live, and for a fixture the golden file has no result for. Absent means the
   * row is not rendered at all — no header, no placeholder, no reserved height — for the same
   * reason the forecast bar above works that way: the layout must not shift between fixtures as
   * the picker cycles through a gameweek.
   */
  goldenScore?: { homeScore: number; awayScore: number } | null
  /** Whether this is the last fixture in the cycle, which turns `Next fixture →` into `Done`. */
  isLastInCycle?: boolean
  onNext?: () => void
}

const FORM_RESULT_LABEL: Record<TeamFormResult, string> = {
  W: 'Win',
  D: 'Draw',
  L: 'Loss',
}

function TeamForm({ form }: { form: TeamFormResult[] }) {
  if (form.length === 0) {
    return <p className="game-edit-screen__form-empty">No games yet</p>
  }

  return (
    <div className="game-edit-screen__form">
      {form.map((result, index) => (
        <span
          key={index}
          className={`game-edit-screen__form-badge game-edit-screen__form-badge--${result.toLowerCase()}`}
          aria-label={FORM_RESULT_LABEL[result]}
        >
          {result}
        </span>
      ))}
    </div>
  )
}

function GameEditScreen({
  game,
  homeScore,
  awayScore,
  homeTeamForm,
  awayTeamForm,
  onScoreChange,
  isBanker,
  onToggleBanker,
  pendingBankerSwitchFrom,
  onConfirmBankerSwitch,
  onCancelBankerSwitch,
  forecast,
  fixtureStatus = 'scheduled',
  goldenScore,
  isLastInCycle = false,
  onNext,
}: GameEditScreenProps) {
  const showForecast = forecast != null && fixtureStatus === 'scheduled'

  const isBankerLanding = useBankerPickedLanding(isBanker)

  return (
    <div className="game-edit-screen">
      {showForecast && (
        <WinProbabilityBar
          homeTeam={game.homeTeam}
          awayTeam={game.awayTeam}
          forecast={forecast}
        />
      )}

      {/* One grid for both rows, so a club's name, its form and its score sit in the same column
          and the colon is the only separator. The two wrappers below are `display: contents`
          — they group the markup, the grid places their children. Headings use the tightest name
          form; everything else on this screen stays on teamDisplayName. */}
      <div className="game-edit-screen__match">
        <div className="game-edit-screen__teams">
          <div className="game-edit-screen__team-col">
            <span className="game-edit-screen__team">{teamPickerName(game.homeTeam)}</span>
            <TeamForm form={homeTeamForm} />
          </div>
          <div className="game-edit-screen__team-col">
            <span className="game-edit-screen__team">{teamPickerName(game.awayTeam)}</span>
            <TeamForm form={awayTeamForm} />
          </div>
        </div>

        <div className="game-edit-screen__scores">
          <ScorePicker
            label={teamDisplayName(game.homeTeam)}
            value={homeScore}
            onChange={(value) => onScoreChange(value, awayScore)}
          />
          <span className="game-edit-screen__colon">:</span>
          <ScorePicker
            label={teamDisplayName(game.awayTeam)}
            value={awayScore}
            onChange={(value) => onScoreChange(homeScore, value)}
          />
        </div>
      </div>

      {goldenScore != null && (
        <GoldenScoreRow
          final={goldenScore}
          onPick={(score) => onScoreChange(score.homeScore, score.awayScore)}
        />
      )}

      {pendingBankerSwitchFrom ? (
        <div className="game-edit-screen__banker-switch-warning" role="alert">
          <p className="game-edit-screen__banker-switch-message">
            You already picked {teamDisplayName(pendingBankerSwitchFrom.homeTeam)} vs{' '}
            {teamDisplayName(pendingBankerSwitchFrom.awayTeam)} as your Banker. Switch to{' '}
            {teamDisplayName(game.homeTeam)} vs {teamDisplayName(game.awayTeam)}?
          </p>
          <div className="game-edit-screen__banker-switch-actions">
            <button
              className="game-edit-screen__banker-switch-cancel"
              onClick={onCancelBankerSwitch}
            >
              Cancel
            </button>
            <button
              className="game-edit-screen__banker-switch-confirm"
              onClick={onConfirmBankerSwitch}
            >
              Switch Banker
            </button>
          </div>
        </div>
      ) : (
        <button
          className={`game-edit-screen__banker ${
            isBanker ? 'game-edit-screen__banker--active' : ''
          } ${isBankerLanding ? 'game-edit-screen__banker--landing' : ''}`}
          onClick={onToggleBanker}
        >
          <Crown aria-hidden="true" />
          {isBanker ? 'Banker picked - Double pts' : 'Make this your Banker'}
        </button>
      )}

      {onNext && (
        <button className="game-edit-screen__next" onClick={onNext}>
          {isLastInCycle ? 'Done' : 'Next fixture →'}
        </button>
      )}
    </div>
  )
}

export default GameEditScreen
